import React, { useEffect, useMemo, useState } from "react";

import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useRouteMatch } from "react-router-dom";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import Select from "react-select";

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import Table from "../../../../components/Table";
import { useClientId } from "../hooks/useClient";
import {
  QUERY_CLIENT_PREFERENCES,
  QUERY_CLIENT_VISITOR_FIELDS,
  QUERY_PREREGISTRATION_LANGUAGES,
} from "../../../../config/graphql/query";
import {
  MUTATION_DELETE_CLIENT_VISITOR_FIELD,
  MUTATION_REORDER_CLIENT_VISITOR_FIELDS,
  UPDATE_CLIENT_PREFERENCES,
} from "../../../../config/graphql/mutation";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

const SortableRow: React.FC<{
  item: IClientVisitorField;
  match: string;
  language: string;
}> = ({ item, match, language }) => {
  const { t } = useTranslation(["visitorPreregistration", "common"]);

  const [visible, setVisible] = useToggle(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#f8f9fa" : undefined,
  };

  const [onDelete] = useMutation(MUTATION_DELETE_CLIENT_VISITOR_FIELD, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_VISITOR_FIELDS,
        variables: { language },
      },
    ],
    variables: { id: item.id },
  });

  const handleDelete = async () => {
    try {
      await onDelete();
      toast.success<string>(t("visitorPreregistration:toast.deleted"));
    } catch (e: any) {
      toast.error<string>(
        e?.message || t("visitorPreregistration:toast.deleteError"),
      );
    } finally {
      setVisible(false);
    }
  };

  return (
    <>
      <tr ref={setNodeRef} style={style}>
        <td
          {...attributes}
          {...listeners}
          style={{
            cursor: "grab",
            width: 40,
            textAlign: "center",
            userSelect: "none",
            color: "#888",
            fontSize: 18,
          }}
          aria-label={t("visitorPreregistration:table.th.drag")}
          title={t("visitorPreregistration:table.th.drag")}
        >
          ☰
        </td>
        <td>{item.type}</td>
        <td>{item.label}</td>
        <td>{item.required ? t("common:yes") : t("common:no")}</td>
        <td className="text-nowrap">
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <Link to={`${match}/${item.id}`}>
              <Button size="sm" variant="primary">
                {t("common:edit")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal show={visible} onHide={setVisible} centered contentClassName="p-2">
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            {t("visitorPreregistration:modal.delete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("visitorPreregistration:modal.delete.body", { label: item.label })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={handleDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const LanguagesSelector: React.FC = () => {
  const { t } = useTranslation(["visitorPreregistration", "languages"]);
  const clientId = useClientId();

  const { data } = useQuery<{ client: { preferences?: IClientPreferences } }>(
    QUERY_CLIENT_PREFERENCES,
    {
      skip: !clientId,
      variables: { id: clientId },
      context: { headers: { ClientId: clientId } },
      fetchPolicy: "cache-and-network",
    },
  );

  const { data: languagesData } = useQuery<{
    preRegistrationLanguages: string[];
  }>(QUERY_PREREGISTRATION_LANGUAGES);

  const [updatePreferences] = useMutation(UPDATE_CLIENT_PREFERENCES, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: [
      { query: QUERY_CLIENT_PREFERENCES, variables: { id: clientId } },
    ],
    awaitRefetchQueries: true,
  });

  const options = useMemo(
    () =>
      (languagesData?.preRegistrationLanguages ?? []).map((value) => ({
        value,
        label: t(`languages:${value}`),
      })),
    [t, languagesData],
  );

  const selected: string[] =
    data?.client?.preferences?.preRegistration?.languages ?? [];

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  const handleChange = async (next: typeof selectedOptions) => {
    const languages = next.map((o) => o.value);

    if (languages.length === 0) {
      toast.error<string>(t("visitorPreregistration:languagesSelector.minOne"));
      return;
    }

    try {
      await updatePreferences({
        variables: {
          input: {
            id: clientId,
            preferences: { preRegistration: { languages } },
          },
        },
      });
      toast.success<string>(t("visitorPreregistration:toast.languagesUpdated"));
    } catch (e: any) {
      toast.error<string>(
        e?.message || t("visitorPreregistration:toast.languagesUpdateError"),
      );
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <label className="d-flex align-items-center mb-2">
          <strong>{t("visitorPreregistration:languagesSelector.label")}</strong>
        </label>
        <Select
          isMulti
          options={options}
          value={selectedOptions}
          onChange={(v) => handleChange((v as typeof selectedOptions) || [])}
          placeholder={t(
            "visitorPreregistration:languagesSelector.placeholder",
          )}
          styles={reactSelectCustomStyles(false)}
        />
        <small className="text-muted font-italic d-block mt-2">
          {t("visitorPreregistration:languagesSelector.helper")}
        </small>
      </div>
    </div>
  );
};

const VisitorFieldList: React.FC = () => {
  const { t } = useTranslation(["visitorPreregistration", "common"]);

  const clientId = useClientId();
  const match = useRouteMatch();
  const language = i18n.language || "en";

  const { data, loading } = useQuery<{
    clientVisitorFields: IClientVisitorField[];
  }>(QUERY_CLIENT_VISITOR_FIELDS, {
    skip: !clientId,
    variables: { language },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [items, setItems] = useState<IClientVisitorField[]>([]);

  useEffect(() => {
    const fields = data?.clientVisitorFields ?? [];
    const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setItems(sorted);
  }, [data]);

  const [onReorder] = useMutation(MUTATION_REORDER_CLIENT_VISITOR_FIELDS, {
    context: { headers: { ClientId: clientId } },
  });

  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((f) => f.id === active.id);
    const newIndex = items.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const previous = items;
    setItems(reordered);
    setSaving(true);

    try {
      await onReorder({
        variables: {
          items: reordered.map((f, index) => ({ id: f.id, order: index })),
          language,
        },
      });
      toast.success<string>(t("visitorPreregistration:toast.reordered"));
    } catch (e: any) {
      setItems(previous);
      toast.error<string>(
        e?.message || t("visitorPreregistration:toast.reorderError"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid">
      <LanguagesSelector />

      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h5 className="d-flex align-items-center">
            {t("visitorPreregistration:title")}
            {saving && (
              <small className="text-muted ml-2 font-italic">
                {t("visitorPreregistration:saving")}
              </small>
            )}
          </h5>
          <p className="text-primary mb-0">
            {t("visitorPreregistration:subtitle")}
          </p>
        </div>
        <Link to={`${match.url}/new`}>
          <Button variant="primary">
            {t("visitorPreregistration:button.add")}
          </Button>
        </Link>
      </div>

      {items.length > 0 && (
        <div
          className="d-flex align-items-center bg-light border rounded px-3 py-2 mb-3"
          role="note"
        >
          <span
            aria-hidden="true"
            style={{ fontSize: 18, color: "#6c757d", marginRight: 12 }}
          >
            ☰
          </span>
          <small className="text-muted mb-0">
            {t("visitorPreregistration:reorderHint")}
          </small>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table>
          <thead>
            <tr>
              <th scope="col" style={{ width: 40 }}>
                <span className="sr-only">
                  {t("visitorPreregistration:table.th.drag")}
                </span>
              </th>
              <th scope="col">{t("visitorPreregistration:table.th.type")}</th>
              <th scope="col">{t("visitorPreregistration:table.th.label")}</th>
              <th scope="col">
                {t("visitorPreregistration:table.th.required")}
              </th>
              <th scope="col">
                {t("visitorPreregistration:table.th.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            <SortableContext
              items={items.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((field) => (
                <SortableRow
                  key={field.id}
                  item={field}
                  match={match.url}
                  language={language}
                />
              ))}
            </SortableContext>
          </tbody>
        </Table>
      </DndContext>

      {!loading && items.length === 0 && (
        <p className="text-muted text-center mb-4">
          {t("visitorPreregistration:empty")}
        </p>
      )}
    </div>
  );
};

export default VisitorFieldList;
